"use client";

import { useEffect } from "react";
import { studioLinkWithAttribution } from "@/lib/attribution-link";
import { env } from "@/lib/env";
import { useAnalyticsConsent } from "./ConsentProvider";

const ANONYMOUS_ID_KEY = "sendbot.anonymousId";
const ATTRIBUTION_TOKEN_KEY = "sendbot.attributionToken";
const AFLINK_TOKEN_KEY = "sendbot.aflinkAttribution:v1";
const AFLINK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const aflinkTokenValid = (value: string | null): value is string =>
  Boolean(value && /^att_[A-Za-z0-9_-]{20,128}$/.test(value));

const getAflinkToken = () => {
  const query = new URLSearchParams(window.location.search).get("_aflink");
  if (aflinkTokenValid(query)) return query;
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(AFLINK_TOKEN_KEY) ?? "null",
    ) as { token?: string; expiresAt?: number } | null;
    if (
      stored &&
      aflinkTokenValid(stored.token ?? null) &&
      typeof stored.expiresAt === "number" &&
      stored.expiresAt > Date.now()
    )
      return stored.token!;
  } catch {
    // Corrupt local state must not interrupt signup.
  }
  window.localStorage.removeItem(AFLINK_TOKEN_KEY);
  return undefined;
};

const createAnonymousId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const getOrCreateAnonymousId = () => {
  const existing = window.localStorage.getItem(ANONYMOUS_ID_KEY);
  if (existing) return existing;
  const created = createAnonymousId();
  window.localStorage.setItem(ANONYMOUS_ID_KEY, created);
  return created;
};

const optionalParam = (params: URLSearchParams, name: string) => {
  const value = params.get(name)?.trim();
  return value || undefined;
};

export function AttributionCapture() {
  const { consentState } = useAnalyticsConsent();

  useEffect(() => {
    if (consentState !== "granted") return;

    const aflinkToken = getAflinkToken();
    if (aflinkToken) {
      window.localStorage.setItem(
        AFLINK_TOKEN_KEY,
        JSON.stringify({
          token: aflinkToken,
          expiresAt: Date.now() + AFLINK_TTL_MS,
        }),
      );
      if (new URLSearchParams(window.location.search).has("_aflink")) {
        const clean = new URL(window.location.href);
        clean.searchParams.delete("_aflink");
        window.history.replaceState(null, "", clean.toString());
      }
    }

    const capture = async () => {
      const params = new URLSearchParams(window.location.search);
      const anonymousId = getOrCreateAnonymousId();
      const attributionToken =
        window.localStorage.getItem(ATTRIBUTION_TOKEN_KEY) ?? undefined;

      try {
        const response = await fetch(
          `${env.NEXT_PUBLIC_SENDBOT_API_URL.replace(/\/$/, "")}/v3/attribution/session`,
          {
            method: "POST",
            credentials: "include",
            keepalive: true,
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              anonymousId,
              attributionToken,
              source: optionalParam(params, "utm_source"),
              medium: optionalParam(params, "utm_medium"),
              campaign: optionalParam(params, "utm_campaign"),
              term: optionalParam(params, "utm_term"),
              content: optionalParam(params, "utm_content"),
              gclid: optionalParam(params, "gclid"),
              gbraid: optionalParam(params, "gbraid"),
              wbraid: optionalParam(params, "wbraid"),
              landingPage: window.location.href,
              referrer: document.referrer || undefined,
              consentState: "granted",
              occurredAt: new Date().toISOString(),
            }),
          },
        );

        if (!response.ok) return;
        const result = (await response.json()) as {
          attributionToken?: string;
          anonymousId?: string;
        };
        if (result.attributionToken) {
          window.localStorage.setItem(
            ATTRIBUTION_TOKEN_KEY,
            result.attributionToken,
          );
        }
        if (result.anonymousId) {
          window.localStorage.setItem(ANONYMOUS_ID_KEY, result.anonymousId);
        }
      } catch {
        // Attribution must never block landing navigation or rendering.
      }
    };

    void capture();
  }, [consentState]);

  useEffect(() => {
    const transferAttribution = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;

      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.hostname !== "studio.sendbot.co") return;
        const token = window.localStorage.getItem(ATTRIBUTION_TOKEN_KEY);
        let destination = studioLinkWithAttribution(
          anchor.href,
          window.location.href,
          consentState,
          token,
        );
        if (consentState === "granted") {
          const aflinkToken = getAflinkToken();
          if (aflinkToken) {
            const targetUrl = new URL(destination);
            targetUrl.searchParams.set("_aflink", aflinkToken);
            destination = targetUrl.toString();
          }
        }
        anchor.href = destination;
      } catch {
        // Ignore malformed or non-HTTP anchors.
      }
    };

    document.addEventListener("click", transferAttribution, true);
    return () =>
      document.removeEventListener("click", transferAttribution, true);
  }, [consentState]);

  return null;
}
